import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Stack } from '@mui/material';
import { VoiceCallFirstMessageMode } from 'flow-schema';
import { useId } from 'react';

import { Alert, Text } from 'common';

import { BlockValueTemplateFieldArea } from '../../../ConfigFields/CodeMirror/FlowTemplate';
import { BlockSelectField } from '../../../ConfigFields/SelectField';
import { useSelectedNodeField } from '../../../ConfigFields/useSelectedNodeField';
import { VoiceCallFirstMessageModeDescriptions } from '../descriptionMappings';
import { useTokenWarning } from './useTokenWarning';

// Required fields
const PROMPT_HELPER_TEXT = `Instructions to the assistant making the call.`;
const FIRST_MESSAGE_HELPER_TEXT = `The first message that the assistant will say when the callee picks up the call.`;
const FIRST_MESSAGE_MODE_HELPER_TEXT = `Determines if the assistant should speak first, wait for the user, or speak first with a model generated message. Defaults to "Assistant waits for user".`;

export function AssistantInstructionsFormFields(props: {
  expanded: boolean;
  onChange: (event: React.SyntheticEvent, isExpanded: boolean) => void;
}) {
  const { expanded, onChange } = props;
  const id = useId();

  const { value: promptValue } = useSelectedNodeField<string>('config.prompt');
  const { tokenCountIsAboveThreshold, threshold } = useTokenWarning({
    promptValue: promptValue || '',
  });

  return (
    <Accordion variant="elevation" expanded={expanded} onChange={onChange}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        id={id}
        aria-controls="instructions-content"
      >
        <Text variant="heading5">Instructions for assistant</Text>
      </AccordionSummary>
      <AccordionDetails sx={{ gap: 2 }}>
        <Stack spacing={2}>
          <BlockValueTemplateFieldArea
            fieldName="config.prompt"
            label="Prompt"
            helperText={PROMPT_HELPER_TEXT}
          />
          {tokenCountIsAboveThreshold && (
            <Alert variant="warning" title="Prompt Length Warning">
              {`Your cumulative prompt length exceeds ${threshold} tokens. We highly recommend reducing the length of your prompt to reduce the probability of the agent hallucinating.`}
            </Alert>
          )}
          <BlockValueTemplateFieldArea
            fieldName="config.firstMessage"
            label="Start of the conversation"
            helperText={FIRST_MESSAGE_HELPER_TEXT}
          />
          <BlockSelectField
            fieldName="config.firstMessageMode"
            label="Who should speak first"
            helperText={FIRST_MESSAGE_MODE_HELPER_TEXT}
            options={Object.values(VoiceCallFirstMessageMode).map((value) => ({
              value,
              label: VoiceCallFirstMessageModeDescriptions[value],
            }))}
            required
          />
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
